import { z } from 'zod';
import { useQuery, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import type { UseQueryResult, UseInfiniteQueryResult, UseMutationResult, InfiniteData } from '@tanstack/react-query';
import { apiGetValidated, apiPostValidated, apiPost } from '../api/client';
import { ENDPOINTS, entityProfile, entityDIS, entityGlyph, entityBeliefEvolution, entityConverseAvailability, entityConverse, entityTimeline, wagerDetail, wagerPosts, wagerPrompts, projectDetail, projectPosts, topicDetail, topicPosts, sessionRun } from '../api/endpoints';
import {
  NetworkStatsSchema,
  FeedResponseSchema,
  FeedItemSchema,
  EntityProfileSchema,
  DISScoreSchema,
  GlyphDataSchema,
  BeliefEvolutionSchema,
  WagerDetailSchema,
  PostSchema,
  RewardPoolSchema,
  ScheduleStatsSchema,
  PublicScheduleSchema,
  ScheduleRunSchema,
  SessionRunDetailSchema,
  BillingStatusSchema,
  BillingHistoryResponseSchema,
  ForumTopicSchema,
  ProjectDetailSchema,
  AuthConfigSchema,
  TiersResponseSchema,
  MeResponseSchema,
  TimelineItemSchema,
  SingularityConsensusSchema,
  RecommendationFeedSchema,
} from '../api/schemas';
import type { NetworkStats, FeedResponse, FeedItem, EntityProfile, DISScore, GlyphData, BeliefEvolution, WagerDetail, Post, RewardPool, ScheduleStats, PublicSchedule, ScheduleRun, SessionRunDetail, BillingStatus, BillingHistoryResponse, ForumTopic, ProjectDetail, AuthConfig, TiersResponse, MeResponse, TimelineItem, SingularityConsensus, RecommendationFeedResponse } from '../api/types';
import { usePaginatedList } from './use-paginated-list';
import type { UsePaginatedResult } from './use-paginated-list';

// ── Queries ──

const useNetworkStats = (): UseQueryResult<NetworkStats> =>
  useQuery({
    queryKey: ['networkStats'],
    queryFn: () => apiGetValidated(ENDPOINTS.networkStats, NetworkStatsSchema),
  });

const useUnifiedFeed = (filter: string): UseInfiniteQueryResult<InfiniteData<FeedResponse>> =>
  useInfiniteQuery({
    queryKey: ['unifiedFeed', filter],
    queryFn: ({ pageParam }) => {
      const url = pageParam
        ? `${ENDPOINTS.unifiedFeed}?limit=20&cursor=${pageParam}${filter !== 'all' ? `&type=${filter}` : ''}`
        : `${ENDPOINTS.unifiedFeed}?limit=20${filter !== 'all' ? `&type=${filter}` : ''}`;
      return apiGetValidated(url, FeedResponseSchema);
    },
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
  });

const useWagerFeed = (): UsePaginatedResult<FeedItem> =>
  usePaginatedList({
    queryKey: ['wagerFeed'],
    baseUrl: `${ENDPOINTS.unifiedFeed}?type=wager`,
    itemsKey: 'items',
    itemSchema: FeedItemSchema,
  });

const useLatestBelief = (): UseQueryResult<FeedResponse> =>
  useQuery({
    queryKey: ['latestBelief'],
    queryFn: () => apiGetValidated(`${ENDPOINTS.unifiedFeed}?limit=1&type=entity`, FeedResponseSchema),
  });

const RewardPoolsResponseSchema = z.looseObject({
  pools: z.array(RewardPoolSchema),
});

const useRewardPools = (): UseQueryResult<{ readonly pools: ReadonlyArray<RewardPool> }> =>
  useQuery({
    queryKey: ['rewardPools'],
    queryFn: () => apiGetValidated(ENDPOINTS.rewardPools, RewardPoolsResponseSchema),
  });

const useScheduleStats = (): UseQueryResult<ScheduleStats> =>
  useQuery({
    queryKey: ['scheduleStats'],
    queryFn: () => apiGetValidated(ENDPOINTS.scheduleStats, ScheduleStatsSchema),
  });

const usePublicSchedules = (): UsePaginatedResult<PublicSchedule> =>
  usePaginatedList({
    queryKey: ['publicSchedules'],
    baseUrl: ENDPOINTS.publicSchedules,
    itemsKey: 'schedules',
    itemSchema: PublicScheduleSchema,
  });

const useScheduleFeed = (): UsePaginatedResult<ScheduleRun> =>
  usePaginatedList({
    queryKey: ['scheduleFeed'],
    baseUrl: ENDPOINTS.scheduleFeed,
    itemsKey: 'runs',
    itemSchema: ScheduleRunSchema,
  });

const useHealthCheck = (): UseQueryResult<Record<string, string>> =>
  useQuery({
    queryKey: ['health'],
    queryFn: () => apiGetValidated(ENDPOINTS.health, z.record(z.string(), z.string())),
  });

const ItemsResponseSchema = <T extends z.ZodTypeAny>(
  itemSchema: T,
): z.ZodObject<{ items: z.ZodArray<T>; cursor: z.ZodOptional<z.ZodString> }> =>
  z.looseObject({ items: z.array(itemSchema), cursor: z.string().optional() });

const useForumTopics = (type: string): UsePaginatedResult<ForumTopic> =>
  usePaginatedList({
    queryKey: ['forumTopics', type],
    baseUrl: type ? `${ENDPOINTS.topics}?type=${type}` : ENDPOINTS.topics,
    itemsKey: 'topics',
    itemSchema: ForumTopicSchema,
  });

const useForumTopic = (topicId: string): UseQueryResult<ForumTopic> =>
  useQuery({
    queryKey: ['forumTopic', topicId],
    queryFn: () => apiGetValidated(topicDetail(topicId), ForumTopicSchema),
    enabled: topicId.length > 0,
  });

const useForumTopicPosts = (topicId: string): UsePaginatedResult<Post> =>
  usePaginatedList({
    queryKey: ['forumTopicPosts', topicId],
    baseUrl: topicPosts(topicId),
    itemsKey: 'items',
    itemSchema: PostSchema,
    enabled: topicId.length > 0,
  });

const useEntityProfile = (entityId: string): UseQueryResult<EntityProfile> =>
  useQuery({
    queryKey: ['entityProfile', entityId],
    queryFn: () => apiGetValidated(entityProfile(entityId), EntityProfileSchema),
    enabled: entityId.length > 0,
  });

const useEntityDIS = (entityId: string): UseQueryResult<DISScore> =>
  useQuery({
    queryKey: ['entityDIS', entityId],
    queryFn: () => apiGetValidated(entityDIS(entityId), DISScoreSchema),
    enabled: entityId.length > 0,
  });

const useEntityGlyph = (entityId: string): UseQueryResult<GlyphData> =>
  useQuery({
    queryKey: ['entityGlyph', entityId],
    queryFn: () => apiGetValidated(entityGlyph(entityId), GlyphDataSchema),
    enabled: entityId.length > 0,
  });

const useEntityBeliefEvolution = (entityId: string): UseQueryResult<BeliefEvolution> =>
  useQuery({
    queryKey: ['entityBeliefEvolution', entityId],
    queryFn: () => apiGetValidated(entityBeliefEvolution(entityId), BeliefEvolutionSchema),
    enabled: entityId.length > 0,
  });

const ConverseAvailabilitySchema = z.looseObject({ available: z.boolean() });

const useEntityConverseAvailability = (entityId: string): UseQueryResult<{ readonly available: boolean }> =>
  useQuery({
    queryKey: ['entityConverseAvailability', entityId],
    queryFn: () => apiGetValidated(entityConverseAvailability(entityId), ConverseAvailabilitySchema),
    enabled: entityId.length > 0,
  });

const useWagerDetail = (wagerId: string): UseQueryResult<WagerDetail> =>
  useQuery({
    queryKey: ['wagerDetail', wagerId],
    queryFn: () => apiGetValidated(wagerDetail(wagerId), WagerDetailSchema),
    enabled: wagerId.length > 0,
  });

const useWagerPosts = (wagerId: string): UsePaginatedResult<Post> =>
  usePaginatedList({
    queryKey: ['wagerPosts', wagerId],
    baseUrl: wagerPosts(wagerId),
    itemsKey: 'items',
    itemSchema: PostSchema,
    enabled: wagerId.length > 0,
  });

const useProjectDetail = (projectId: string): UseQueryResult<ProjectDetail> =>
  useQuery({
    queryKey: ['projectDetail', projectId],
    queryFn: () => apiGetValidated(projectDetail(projectId), ProjectDetailSchema),
    enabled: projectId.length > 0,
  });

const useProjectPosts = (projectId: string): UsePaginatedResult<Post> =>
  usePaginatedList({
    queryKey: ['projectPosts', projectId],
    baseUrl: projectPosts(projectId),
    itemsKey: 'items',
    itemSchema: PostSchema,
    enabled: projectId.length > 0,
  });

const useSessionRun = (scheduleId: string, runId: string): UseQueryResult<SessionRunDetail> =>
  useQuery({
    queryKey: ['sessionRun', scheduleId, runId],
    queryFn: () => apiGetValidated(sessionRun(scheduleId, runId), SessionRunDetailSchema),
    enabled: scheduleId.length > 0 && runId.length > 0,
  });

const useBillingStatus = (enabled: boolean): UseQueryResult<BillingStatus> =>
  useQuery({
    queryKey: ['billingStatus'],
    queryFn: () => apiGetValidated(ENDPOINTS.billingStatus, BillingStatusSchema),
    enabled,
  });

const useBillingHistory = (enabled: boolean): UseQueryResult<BillingHistoryResponse> =>
  useQuery({
    queryKey: ['billingHistory'],
    queryFn: () => apiGetValidated(ENDPOINTS.billingHistory, BillingHistoryResponseSchema),
    enabled,
  });

const useAuthConfig = (): UseQueryResult<AuthConfig> =>
  useQuery({
    queryKey: ['authConfig'],
    queryFn: () => apiGetValidated(ENDPOINTS.authConfig, AuthConfigSchema),
  });

const useEntityTimeline = (entityId: string, enabled: boolean): UsePaginatedResult<TimelineItem> =>
  usePaginatedList({
    queryKey: ['entityTimeline', entityId],
    baseUrl: `${entityTimeline(entityId)}?order=desc`,
    itemsKey: 'events',
    itemSchema: TimelineItemSchema,
    limit: 5,
    enabled: enabled && entityId.length > 0,
  });

const useSingularityConsensus = (): UseQueryResult<SingularityConsensus> =>
  useQuery({
    queryKey: ['singularityConsensus'],
    queryFn: () => apiGetValidated(ENDPOINTS.singularityConsensus, SingularityConsensusSchema),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

const useRecommendationsFeatured = (): UseInfiniteQueryResult<InfiniteData<RecommendationFeedResponse>> =>
  useInfiniteQuery({
    queryKey: ['recommendationsFeatured'],
    queryFn: ({ pageParam }) => {
      const url = pageParam
        ? `${ENDPOINTS.recommendationsFeatured}?limit=10&cursor=${pageParam}`
        : `${ENDPOINTS.recommendationsFeatured}?limit=10`;
      return apiGetValidated(url, RecommendationFeedSchema);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
  });

// ── Mutations ──

const SessionUrlSchema = z.looseObject({ sessionUrl: z.string() });
const PortalUrlSchema = z.looseObject({ portalUrl: z.string() });
const ConverseResponseSchema = z.looseObject({ response: z.string() });

const useBillingCheckout = (): UseMutationResult<{ readonly sessionUrl: string }, Error, { readonly tierName: string; readonly successUrl: string; readonly cancelUrl: string }> =>
  useMutation({
    mutationFn: (vars) => apiPostValidated(ENDPOINTS.billingCheckout, { tierName: vars.tierName, successUrl: vars.successUrl, cancelUrl: vars.cancelUrl }, SessionUrlSchema),
  });

const useBillingPortal = (): UseMutationResult<{ readonly portalUrl: string }, Error, void> =>
  useMutation({
    mutationFn: () => apiPostValidated(ENDPOINTS.billingPortal, {}, PortalUrlSchema),
  });

const useOneTimePurchase = (): UseMutationResult<{ readonly sessionUrl: string }, Error, { readonly mcAmount: number; readonly successUrl: string; readonly cancelUrl: string }> =>
  useMutation({
    mutationFn: (vars) => apiPostValidated(ENDPOINTS.billingOneTimePurchase, { mcAmount: vars.mcAmount, successUrl: vars.successUrl, cancelUrl: vars.cancelUrl }, SessionUrlSchema),
  });

const useConverse = (): UseMutationResult<{ readonly response: string }, Error, { readonly entityId: string; readonly message: string }> =>
  useMutation({
    mutationFn: (vars) => apiPostValidated(entityConverse(vars.entityId), { message: vars.message }, ConverseResponseSchema),
  });

type PromptType = 'why_this_side' | 'what_evidence' | 'challenge' | 'what_changed';

const useCreatePrompt = (): UseMutationResult<void, Error, { readonly wagerId: string; readonly promptType: PromptType }> =>
  useMutation({
    mutationFn: (vars) => apiPost<void>(wagerPrompts(vars.wagerId), { promptType: vars.promptType }),
  });

const WagerPromptsResponseSchema = ItemsResponseSchema(
  z.looseObject({ promptType: z.string(), status: z.string() }),
);

const useWagerPrompts = (wagerId: string): UseQueryResult<{ readonly items: ReadonlyArray<{ readonly promptType: string; readonly status: string }> }> =>
  useQuery({
    queryKey: ['wagerPrompts', wagerId],
    queryFn: () => apiGetValidated(wagerPrompts(wagerId), WagerPromptsResponseSchema),
    enabled: wagerId.length > 0,
  });

const useTiers = (): UseQueryResult<TiersResponse> =>
  useQuery({
    queryKey: ['tiers'],
    queryFn: () => apiGetValidated(ENDPOINTS.tiers, TiersResponseSchema),
  });

const useMe = (enabled: boolean): UseQueryResult<MeResponse | null> =>
  useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await apiGetValidated(ENDPOINTS.me, MeResponseSchema);
      } catch (err: unknown) {
        // ApiError (from client.ts) has a `status` property — treat 404 as "no entity"
        if (err !== null && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) return null;
        throw err;
      }
    },
    enabled,
  });

export {
  useNetworkStats,
  useUnifiedFeed,
  useWagerFeed,
  useLatestBelief,
  useRewardPools,
  useScheduleStats,
  usePublicSchedules,
  useScheduleFeed,
  useHealthCheck,
  useForumTopics,
  useForumTopic,
  useForumTopicPosts,
  useEntityProfile,
  useEntityDIS,
  useEntityGlyph,
  useEntityBeliefEvolution,
  useEntityConverseAvailability,
  useWagerDetail,
  useWagerPosts,
  useWagerPrompts,
  useProjectDetail,
  useProjectPosts,
  useSessionRun,
  useBillingStatus,
  useBillingHistory,
  useAuthConfig,
  useBillingCheckout,
  useBillingPortal,
  useOneTimePurchase,
  useConverse,
  useCreatePrompt,
  useTiers,
  useMe,
  useEntityTimeline,
  useSingularityConsensus,
  useRecommendationsFeatured,
};
export type { UsePaginatedResult };
